import { useTranslation } from 'react-i18next'
import { Popover } from '@mui/material'
import { useAppStore } from '@/store/appStore'
import { trackEvent } from '@/services/appInsights'
import type { DocumentCollectionBundleItem } from '@/types/api'

interface CrosswalksMenuProps {
  open: boolean
  anchorEl: HTMLElement | null
  collection: DocumentCollectionBundleItem | undefined
  onClose: () => void
}

export function CrosswalksMenu({ open, anchorEl, collection, onClose }: CrosswalksMenuProps) {
  const { t } = useTranslation()
  const { setPdfViewer } = useAppStore()

  const handleItemClick = (systemFileName: string, displayName: string) => {
    trackEvent('Crosswalk Document Opened', { systemFileName, displayName })
    setPdfViewer({ systemFileName, title: displayName })
    onClose()
  }

  const items = collection?.items ?? []

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{
        paper: {
          className: 'crosswalks-menu-paper',
          sx: { ml: '4px', borderRadius: '6px', boxShadow: 'var(--shadow-menu-soft)' },
        },
      }}
    >
      <div className="crosswalks-menu">
        <div className="crosswalks-menu-header">{t('crosswalks.title')}</div>

        {items.length === 0 && (
          <div className="crosswalks-menu-empty">{t('crosswalks.noItems')}</div>
        )}

        {items.length > 0 && (
          <div className="crosswalks-menu-list">
            {items.map((item) => (
              <button
                key={item.id}
                className="crosswalks-menu-item"
                onClick={() => handleItemClick(item.systemFileName, item.displayName)}
                title={item.description ?? item.displayName}
              >
                <span className="crosswalks-menu-item-label">{item.displayName}</span>
                {item.description && (
                  <span className="crosswalks-menu-item-description">{item.description}</span>
                )}
              </button>
            ))}
          </div>
        )}
      </div>
    </Popover>
  )
}
